<template>
  <div v-if="loaded" class="h-100" @click="clearSelection">
    <mdb-row class="text-left">
      <mdb-btn
        rounded
        size="sm"
        gradient="aqua"
        icon="angle-double-left"
        class="mb-3"
        @click="$router.push({ name: 'orders' })"
      >Back to table</mdb-btn>
    </mdb-row>
    <mdb-container class="order-container order-container-info overflow-auto">
      <mdb-row>
        <mdb-col col="6" class="pt-2 rounded" :class="{'aqua-gradient': isClientContext}">
          <h5>
            Client
            <mdb-btn
              icon="plus"
              gradient="aqua"
              v-if="isAgentContext"
              size=sm
              rounded
              title="Link new client order"
              class="ml-5 my-0"
              @click="addOrder()"
            />
          </h5>
        </mdb-col>
        <mdb-col col="6" class="pt-2 rounded" :class="{'deep-blue-gradient': isAgentContext}">
          <h5>
            Agent
            <mdb-btn
              icon="plus"
              gradient="deep-blue"
              v-if="isClientContext"
              size=sm
              rounded
              title="Link new agent order"
              class="ml-5 my-0"
              @click="addOrder()"
            />
          </h5>
        </mdb-col>
      </mdb-row>
      <mdb-row class="mt-3">
        <mdb-col col="2">
          <ul class="list-unstyled">
            <li
              v-for="(order, i) in clientOrders"
              :key="`client-order-${i}`"
              class="py-1"
              :class="{ 'highlighted-entity': selectedOrders.includes(order.info.uuid) }"
            >
              <order-order
                :context="context"
                :order="order"
                @selected="selectOrder(order)"
                @deleted="getOrderInfo()"
              />
            </li>
          </ul>
        </mdb-col>
        <mdb-col col="2">
          <ul class="list-unstyled">
            <template v-for="(order, i) in clientOrders">
              <li
                v-for="(request, j) in order.requests"
                :key="`client-request-${i}-${j}`"
                class="mb-2 hover-pointer"
                :class="{ 'active-entity': selectedUuid === request.uuid, 'highlighted-entity': selectedRequests.includes(request.uuid) }"
                @click.stop="selectRequest(request, order.info.uuid)"
              >
                <order-request
                  :context="context"
                  :request="request"
                  :order="order"
                />
              </li>
            </template>
          </ul>
        </mdb-col>
        <mdb-col col="2" class="border-right">
          <ul class="list-unstyled">
            <template v-for="(order, i) in clientOrders">
              <template v-for="(request, j) in order.requests">
                <li
                  v-for="(quote, k) in request.quotes"
                  :key="`client-quote-${i}-${j}-${k}`"
                  class="mb-2 hover-pointer"
                  :class="{ 'active-entity': selectedUuid === quote.uuid, 'highlighted-entity': selectedQuotes.includes(quote.uuid) }"
                  @click.stop="selectQuote(quote, request.uuid, order.info.uuid)"
                >
                  <order-quote
                    :context="context"
                    :quote="quote"
                    :request="request"
                    @create-mail-text="showMailTextModal($event)"
                    @rate-price-changed="getOrderInfo"
                  />
                </li>
              </template>
            </template>
          </ul>
        </mdb-col>

        <mdb-col col="2" class="border-left">
          <ul class="list-unstyled">
            <template v-for="(order, i) in agentOrders">
              <template v-for="(request, j) in order.requests">
                <li
                  v-for="(quote, k) in request.quotes"
                  :key="`agent-quote-${i}-${j}-${k}`"
                  class="mb-2 hover-pointer"
                  :class="{ 'active-entity': selectedUuid === quote.uuid, 'highlighted-entity': selectedQuotes.includes(quote.uuid) }"
                  @click.stop="selectQuote(quote, request.uuid, order.info.uuid)"
                >
                  <order-quote
                    :context="context"
                    :quote="quote"
                    :request="request"
                    @create-mail-text="showMailTextModal($event)"
                    @rate-price-changed="getOrderInfo"
                  />
                </li>
              </template>
            </template>
          </ul>
        </mdb-col>
        <mdb-col col="2">
          <ul class="list-unstyled">
            <template v-for="(order, i) in agentOrders">
              <li
                v-for="(request, j) in order.requests"
                :key="`agent-request-${i}-${j}`"
                class="mb-2 hover-pointer"
                :class="{ 'active-entity': selectedUuid === request.uuid, 'highlighted-entity': selectedRequests.includes(request.uuid) }"
                @click.stop="selectRequest(request, order.info.uuid)"
              >
                <order-request
                  :context="context"
                  :request="request"
                  :order="order"
                />
              </li>
            </template>
          </ul>
        </mdb-col>
        <mdb-col col="2">
          <ul class="list-unstyled">
            <li
              v-for="(order, i) in agentOrders"
              :key="`agent-order-${i}`"
              class="py-1"
              :class="{ 'highlighted-entity': selectedOrders.includes(order.info.uuid) }"
            >
              <order-order
                :context="context"
                :order="order"
                @selected="selectOrder(order)"
                @deleted="getOrderInfo()"
              />
            </li>
          </ul>
        </mdb-col>
      </mdb-row>
    </mdb-container>
    <div @click.stop>
      <mdb-container class="order-container order-container-edit mt-3 px-0">
        <order-entity-properties
          v-if="selectedUuid"
          :properties="selectedProperties"
          @props-change="propertiesChanged($event)"
        />
      </mdb-container>
    </div>
    <mdb-modal :show="mailTextModalShow" @close="hideMailTextModal">
      <mdb-modal-header class="aqua-gradient">
        <mdb-modal-title class="text-white">Suggested Email Text</mdb-modal-title>
      </mdb-modal-header>
      <mdb-modal-body>
        <mdb-input
          class="mail-text-modal-textarea"
          type="textarea"
          :value="mailTextModalText"
          @input="handleMailTextInput"
        />
      </mdb-modal-body>
      <mdb-modal-footer>
        <mdb-btn
          rounded
          gradient="aqua"
          @click.native="onCopyToClipboardClick"
          title="Copy text to clipboard"
        >Copy To Clipboard</mdb-btn>
        <mdb-btn
          rounded
          gradient="sunny-morning"
          @click.native="hideMailTextModal"
          title="Close dialog"
        >Close</mdb-btn>
      </mdb-modal-footer>
    </mdb-modal>
    <order-select-modal/>
  </div>
</template>

<script>
import {
  mdbContainer,
  mdbRow,
  mdbCol,
  mdbBtn,
  mdbIcon,
  mdbModal,
  mdbModalHeader,
  mdbModalTitle,
  mdbModalBody,
  mdbModalFooter,
  mdbInput
} from 'mdbvue'
import OrderOrder from './OrderOrder'
import OrderRequest from './OrderRequest'
import OrderQuote from './OrderQuote'
import OrderEntityProperties from './OrderEntityProperties'
import {OrderTypes} from '../enums'
import {mapActions} from 'vuex'
import {errorCatch} from '../utils'
import OrderSelectModal from './OrderSelectModal'

export default {
  name: 'Order',
  data: function () {
    return {
      loaded: false,
      mailTextModalShow: false,
      mailTextModalText: '',
      order: null,
      orderInfo: null,
      selectedUuid: null,
      selectedProperties: [],
      selectedOrders: [],
      selectedRequests: [],
      selectedQuotes: []
    }
  },
  props: {
    uuid: {
      type: String,
      required: true
    }
  },
  computed: {
    context () {
      return this.order.client ? OrderTypes.CLIENT : OrderTypes.AGENT
    },
    isAgentContext () {
      return this.context === OrderTypes.AGENT
    },
    isClientContext () {
      return this.context === OrderTypes.CLIENT
    },
    clientOrders () {
      switch (this.context) {
        case OrderTypes.CLIENT:
          return this.orderInfo ? [this.orderInfo] : []
        case OrderTypes.AGENT:
          return this.orderInfo ? this.orderInfo.orders : []
      }
    },
    agentOrders () {
      switch (this.getContext()) {
        case OrderTypes.AGENT:
          return this.orderInfo ? [this.orderInfo] : []
        case OrderTypes.CLIENT:
          return this.orderInfo ? this.orderInfo.orders : []
      }
    }
  },
  methods: {
    ...mapActions(['apiOrderGetInfo', 'apiNodeGetProperties', 'apiNodeSetProperties']),
    ...mapActions('calc', ['setLinked', 'setClone', 'setClient', 'setAgent']),
    getContext (ord = null) {
      const order = ord || this.order
      return order.client ? OrderTypes.CLIENT : OrderTypes.AGENT
    },
    getOrderInfo (ord = null) {
      const order = ord || this.order
      return this.apiOrderGetInfo(order.info.uuid)
        .then(orderInfo => { this.orderInfo = orderInfo })
        .catch(errorCatch('Get Order Info failed.', this))
    },
    getOrderInfoByUuid (uuid) {
      return this.apiOrderGetInfo(uuid)
        .then(orderInfo => {
          this.order = orderInfo
          this.orderInfo = orderInfo
        })
        .catch(errorCatch('Get Order Info failed.', this))
    },
    selectOrder (order) {
      this.clearSelection()
      this.getOrderInfo(order).then(() => { this.order = order })
    },
    clearSelection () {
      this.$set(this, 'selectedUuid', null)
      this.$set(this, 'selectedOrders', [])
      this.$set(this, 'selectedRequests', [])
      this.$set(this, 'selectedQuotes', [])
    },
    selectRequest (request, orderUuid) {
      this.$set(this, 'selectedUuid', request.uuid)
      this.$set(this, 'selectedOrders', [orderUuid].concat(request.quotes.flatMap(q => q.linked.map(l => l.order))))
      this.$set(this, 'selectedRequests', request.quotes.flatMap(q => q.linked.map(l => l.request)))
      this.$set(
        this,
        'selectedQuotes',
        request.quotes.map(q => q.uuid).concat(request.quotes.flatMap(q => q.linked.map(l => l.quote)))
      )
      this.getProperties()
    },
    selectQuote (quote, requestUuid, orderUuid) {
      this.$set(this, 'selectedUuid', quote.uuid)
      this.$set(this, 'selectedOrders', [orderUuid].concat(quote.linked.map(l => l.order)))
      this.$set(this, 'selectedRequests', [requestUuid].concat(quote.linked.map(l => l.request)))
      this.$set(this, 'selectedQuotes', quote.linked.map(l => l.quote))
      this.getProperties()
    },
    getProperties () {
      if (this.selectedUuid) {
        return this.apiNodeGetProperties(this.selectedUuid)
          .then(props => {
            this.selectedProperties = props
          })
          .catch(errorCatch('Get Node Properties Failed.', this))
      }
    },
    propertiesChanged (props) {
      const propsData = {}
      props.forEach(prop => {
        propsData[prop.key] = prop.value
      })
      // TODO: Check why it fires several times
      return this.apiNodeSetProperties({ uuid: this.selectedUuid, props: propsData })
        .then(() => {
          this.getOrderInfo(this.order)
            .then(() => this.getProperties())
        })
        .catch(errorCatch('Node Properties Change failed.', this))
    },
    addOrder () {
      this.setLinked(this.order)
      this.$router.push('/calc')
    },
    showMailTextModal (text) {
      this.mailTextModalText = text
      this.mailTextModalShow = true
    },
    hideMailTextModal () {
      this.mailTextModalText = ''
      this.mailTextModalShow = false
    },
    handleMailTextInput (text) {
      this.mailTextModalText = text
    },
    onCopyToClipboardClick () {
      navigator.clipboard.writeText(this.mailTextModalText)
      this.$notify.info({
        title: 'Text copied to Clipboard',
        message: 'Now you can paste it in your mail client',
        position: 'top right',
        timeOut: 10000,
        closeButton: true
      })
    }
  },
  beforeRouteUpdate (to, from, next) {
    this.getOrderInfoByUuid(to.params.uuid)
    next()
  },
  mounted () {
    this.getOrderInfoByUuid(this.uuid).then(() => { this.loaded = true })
    this.$bus.$on('clone-request', clone => {
      this.setClone(clone)
      if (clone.order === null) {
        this.setLinked(this.order)
      }
      switch (clone.context) {
        case OrderTypes.CLIENT:
          this.setClient(clone.type === OrderTypes.CLIENT ? this.order.client.name : null)
          this.setAgent(null)
          break
        case OrderTypes.AGENT:
          this.setClient(null)
          this.setAgent(this.order.agent.name)
          break
      }
    })
    this.$bus.$on('order-name-change', () => {
      this.getOrderInfo()
    })
  },
  components: {
    mdbContainer,
    mdbRow,
    mdbCol,
    OrderOrder,
    OrderRequest,
    OrderQuote,
    OrderEntityProperties,
    mdbBtn,
    mdbIcon,
    mdbModal,
    mdbModalHeader,
    mdbModalTitle,
    mdbModalBody,
    mdbModalFooter,
    mdbInput,
    OrderSelectModal
  }
}
</script>

<style lang="sass" scoped>
.order-container
  height: 100%

  &.order-container-info
    height: 50%

    .hover-pointer:hover
      cursor: pointer

    .active-context
      background-color: lightcyan

  &.order-container-edit
    height: 30%

li
  &.active-entity
    border: darkcyan 3px dashed

  &.highlighted-entity
    border: darkcyan 1px solid
</style>



// WEBPACK FOOTER //
// src/components/Order.vue